import { Translations } from './types';

export const en: Translations = {
  header: {
    about: 'About',
    ideas: 'Ideas',
    submitIdea: 'Submit an Idea',
    admin: 'Admin',
    logout: 'Logout',
    loginSignUp: 'Login / Sign Up',
    loading: 'Loading...',
  },
  footer: {
    contact: 'Contact',
    quickLinks: 'Quick Links',
    needHelp: 'Need help? Reach out to',
    allRightsReserved: 'All rights reserved.',
    privacyPolicy: 'Privacy Policy',
    evanstonDescription:
      'Putting Assets to Work - A collaborative community engagement initiative to reimagine and revitalize key civic assets in Evanston, creating vibrant spaces that serve our diverse community.',
  },
  spaceSelector: {
    filterBySpace: 'Filter by Space',
    allSpaces: 'All Spaces',
  },
  filterControls: {
    sortBy: 'Sort By',
    mostUpvoted: 'Most Upvoted',
    mostRecent: 'Most Recent',
    filterByTags: 'Filter by Tags',
    search: 'Search',
    clear: 'Clear',
    clearAllTags: 'Clear all tags',
  },
  ideaCard: {
    anonymous: 'Anonymous',
    unknownUser: 'Unknown User',
  },
  ideaForm: {
    successMessage: 'Your idea was submitted successfully! Thank you for your contribution.',
    loginToSubmitTitle: 'Login to Submit Your Idea',
    loginToSubmitSubtitle: 'Please login or create an account to submit your idea.',
    backToForm: 'Back to Form',
    submitAsPrefix: 'You will submit this idea as',
    titleLabel: 'Title',
    titlePlaceholder: 'Enter a clear, descriptive title for your idea',
    characters: 'characters',
    descriptionLabel: 'Description',
    descriptionPlaceholder: 'Describe your idea in detail. What would it involve? How would it benefit the community?',
    selectSpace: 'Select Space',
    chooseSpacePlaceholder: 'Choose which space this idea is for',
    categoryTag: 'Category Tag',
    selectTagPlaceholder: 'Select a tag for your idea',
    anonymousTooltipOn:
      'Your name will be hidden from other users, but administrators will still be able to see who submitted this idea.',
    anonymousTooltipOff: 'Click to hide your name from other users',
    postingAnonymously: 'Posting anonymously',
    postingAsPrefix: 'Posting as',
    adminsCanSee: '(Admins can still see your identity)',
    addImageTitle: 'Add an Image (Optional)',
    imageUploadFallback:
      'Add a visual representation of your idea. Images help others understand your vision better. Supported formats: JPEG, PNG, GIF, etc. (Max size: 15MB)',
    uploadImage: 'Upload Image',
    compressingImage: 'Compressing image...',
    remove: 'Remove',
    submitIdea: 'Submit Idea',
    loginToSubmit: 'Login to Submit',
    titleRequired: 'Title is required',
    descriptionRequired: 'Description is required',
    tagRequired: 'Please select a tag',
    spaceRequired: 'Please select a space for your idea',
    noSpaces: 'No spaces available. Please contact support.',
    imageTooLarge: 'Image size exceeds 15MB limit. Please select a smaller image.',
    invalidImage: 'Please select a valid image file (JPEG, PNG, etc.).',
    imageProcessFailed: 'Failed to process the image. Please try again with a different image.',
    submitFailed: 'Failed to submit your idea. Please try again.',
    tagsLoadFailed: 'Failed to load available tags. Please refresh and try again.',
    spacesLoadFailed: 'Failed to load available spaces. Please refresh and try again.',
  },
  comments: {
    title: 'Discussion',
    noComments: 'No comments yet. Be the first to share your thoughts!',
    loginToPostTitle: 'Login to Post Your Comment',
    loginToPostSubtitle: 'Please login or create an account to post your comment.',
    backToCommentForm: 'Back to Comment Form',
    addYourComment: 'Add Your Comment',
    postAsPrefix: 'You will post as',
    yourComment: 'Your Comment',
    commentPlaceholder: 'Share your thoughts about this idea...',
    commentEmpty: 'Comment text cannot be empty',
    submitFailed: 'Failed to submit comment. Please try again.',
    commentingAnonymously: 'Commenting anonymously',
    commentingAsPrefix: 'Commenting as',
    anonymousTooltipOn:
      'Your name will be hidden from other users, but administrators will still be able to see who posted this comment.',
    postComment: 'Post Comment',
    loginToPost: 'Login to Post',
  },
  ideaDetail: {
    backToIdeas: 'Back to Ideas',
    postedOn: 'Posted on',
    upvote: 'Upvote',
    upvoted: 'Upvoted',
    ideaNotFound: 'Idea not found.',
    visualizeIdea: 'Visualize your idea here',
  },
  dynamicAbout: {
    meetingsTitle: 'PAW Community Meetings',
    past: 'Past',
    presentation: 'Presentation',
    notes: 'Notes',
    eventInfo: 'Event Info',
    viewDetails: 'View Details',
    downloadFlyer: 'Download Flyer',
    loadFailed: 'Failed to load content',
    comingSoon: 'Coming soon!',
  },
  documentGate: {
    signInTitle: 'Sign in to access these documents',
    signInSubtitle:
      'You must be signed in and agree to the document release terms before downloading project plans and CAD files.',
    signIn: 'Sign In',
    signedNotice: 'You have signed the document release agreement. Downloads are available below.',
  },
  loginForm: {
    loginTab: 'Login',
    signUpTab: 'Sign Up',
    email: 'Email',
    password: 'Password',
    fullName: 'Full Name',
    loginButton: 'Login',
    signUpButton: 'Sign Up',
    or: 'OR',
    signInWithGoogle: 'Sign in with Google',
    signUpWithGoogle: 'Sign up with Google',
    needAccount: 'Need an account? Sign up',
    haveAccount: 'Already have an account? Login',
    fullNameHelper: 'Required for email sign-up. For Google sign-up, your Google name will be used.',
    passwordHelper: 'Password must be at least 6 characters',
    zipCode: 'Zip Code',
    zipCodeHelper: 'Enter your 5-digit zip code',
    ward: 'Ward',
    wardPrefix: 'Ward',
    unsure: 'Unsure',
    wardOutside: 'Other/Outside Evanston',
    address: 'Street Address',
    addressHelper: 'Optional — helps us understand which neighborhoods are participating',
    raceEthnicity: 'Race/Ethnicity',
    demographicDisclaimer:
      'The information collected here is used solely to gather community input regarding the future of the three public assets and related planning efforts. It will not be used for any other purpose.',
    connectionToPrefix: 'Connection to',
    pleaseSpecify: 'Please specify',
    loggedIn: 'Logged In',
    loggedInAsPrefix: 'Logged in as',
    connectionPrefix: 'Connection:',
    completeProfileTitle: 'Complete Your Profile',
    completeProfileHelpPrefix: 'Help us understand your connection to the',
    completeProfileHelpSuffix: 'project.',
    updateProfileTitle: 'Update Profile',
    completeProfileButton: 'Complete Profile',
    updateProfileButton: 'Update Profile',
    continue: 'Continue',
    logOut: 'Log Out',
    emailRequired: 'Email is required',
    passwordRequired: 'Password is required',
    passwordMinLength: 'Password must be at least 6 characters',
    fullNameRequired: 'Full name is required',
    connectionRequiredPrefix: 'Please select your connection to',
    connectionSpecifyPrefix: 'Please specify your connection to',
    zipRequired: 'Zip code is required',
    zipFormat: 'Zip code must be a 5-digit number',
    wardRequired: 'Please select your ward',
    connectionBeforeGoogleSuffix: 'before signing up with Google',
    zipBeforeGoogle: 'Please enter your zip code before signing up with Google',
    wardBeforeGoogle: 'Please select your ward before signing up with Google',
    raceEthnicityRequired: 'Please select your race/ethnicity',
    raceEthnicitySpecifyRequired: 'Please specify your race/ethnicity',
    raceEthnicityBeforeGoogle: 'Please select your race/ethnicity before signing up with Google',
    loginSuccess: 'Login successful!',
    accountCreated: 'Account created successfully!',
    profileUpdated: 'Profile updated successfully!',
    loggedOut: 'Logged out successfully!',
    loginFailed: 'Failed to login. Please check your credentials.',
    signupFailed: 'Failed to create account. Please try again.',
    googleSignInFailed: 'Failed to login with Google. Please try again.',
    googleSignUpFailed: 'Failed to sign up with Google. Please try again.',
    profileUpdateFailed: 'Failed to update profile. Please try again.',
    logoutFailed: 'Failed to logout. Please try again.',
    mustBeLoggedIn: 'You must be logged in to update your profile',
    forgotPassword: 'Forgot your password?',
    resetEmailSent: 'If an account exists for that email, we sent a password reset link. Check your inbox and spam folder.',
    resetFailed: 'We could not send the reset email. Please try again.',
    authErrors: {
      invalidCredential: 'Incorrect email or password. Double-check both, or use "Forgot your password?" to reset it. If you signed up with Google, use the "Sign in with Google" button instead.',
      emailAlreadyInUse: 'An account with this email already exists. Please log in instead.',
      tooManyRequests: 'Too many attempts — this account is temporarily locked. Reset your password or try again later.',
      invalidEmail: 'That email address does not look valid. Check for typos or extra spaces.',
      weakPassword: 'That password is too weak. Please use at least 6 characters.',
      networkFailed: 'Network error. Check your connection and try again.',
      popupClosed: 'The Google sign-in window was closed before finishing. Please try again.',
      userDisabled: 'This account has been disabled. Please contact the site team for help.',
    },
  },
  // English is the source language; backend labels render verbatim.
  spaces: {},
  tags: {},
  connectionOptions: {},
  raceEthnicityOptions: {},
  home: {
    fallbackTitle: 'Community Ideas',
    fallbackSubtitle: 'Browse and vote on ideas for community development',
    upvoteAdded: 'Upvote added!',
    upvoteRemoved: 'Upvote removed',
    upvoteFailed: 'Failed to register your vote. Please try again.',
    loginToVote: 'Please log in to vote on ideas',
  },
  submit: {
    fallbackTitle: 'Submit Your Idea',
    fallbackSubtitle: 'Share your vision for the future of our community space.',
    fallbackProjectInfoTitle: 'About This Project',
    fallbackProjectInfoDescription: 'A community-driven initiative to transform our space.',
    fallbackLookingForTitle: "What We're Looking For",
    fallbackLookingForDescription: 'Creative ideas for public spaces and community features.',
    fallbackImpactTitle: 'Your Impact',
    fallbackImpactDescription: 'Community input is essential to creating a space that reflects local needs.',
  },
  submissionWindow: {
    countdownLabel: 'Ideas, votes, and comments close in',
    days: 'days',
    hours: 'hours',
    minutes: 'minutes',
    seconds: 'seconds',
    closedTitle: 'This round of community input is paused',
    closedBody: 'We are not collecting new ideas, votes, or comments at this time. The Putting Assets to Work team is reviewing everything the community has shared and developing recommendations for the Civic Center, Noyes Cultural Arts Center, and Police & Fire Headquarters. There will be more opportunities to weigh in once draft recommendations are ready.',
    commentsClosed: 'Comments are closed. We are not collecting new comments at this time.',
    votes: 'votes',
  },
  common: {
    loading: 'Loading...',
    error: 'Error',
    cancel: 'Cancel',
    save: 'Save',
  },
};
